import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.css';

const CAPSULE_TABS = [
  {
    id: 'home',
    label: 'Home',
    icon: (color) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill={color}>
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
      </svg>
    ),
    route: '/profile',
  },
  {
    id: 'categories',
    label: 'Categories',
    disabledKey: 'hasCategories',
    icon: (color) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill={color}>
        <path d="M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.87L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z" />
      </svg>
    ),
    scrollTo: 'section-categories',
  },
  {
    id: 'albums',
    label: 'Albums',
    disabledKey: 'hasAlbums',
    icon: (color) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill={color}>
        <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" />
      </svg>
    ),
    scrollTo: 'section-albums',
  },
  {
    id: 'playlist',
    label: 'Playlist',
    disabledKey: 'hasPlaylists',
    icon: (color) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill={color}>
        <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z" />
      </svg>
    ),
    scrollTo: 'section-playlist',
  },
];

const WATCHLIST_TAB = {
  id: 'watchlist',
  label: 'Watchlist',
  icon: (color) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={color}>
      <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2zm0 15l-5-2.18L7 18V5h10v13z" />
    </svg>
  ),
  route: '/watchlist',
};

const DISABLED_COLOR = 'rgba(128, 128, 128, 0.5)';

function BottomBar({ onScrollToSection, activeSection, hasCategories, hasAlbums, hasPlaylists }) {
  const navigate = useNavigate();

  const dataFlags = { hasCategories, hasAlbums, hasPlaylists };

  const handleTabClick = (tab, disabled) => {
    if (disabled) return;
    if (tab.route) {
      navigate(tab.route);
      if (tab.id === 'home' && onScrollToSection) {
        onScrollToSection('home');
      }
    } else if (tab.scrollTo && onScrollToSection) {
      onScrollToSection(tab.id);
    }
  };

  const isWatchlistActive = activeSection === 'watchlist';
  const watchlistColor = isWatchlistActive ? '#5AA5EF' : 'rgba(255, 255, 255, 0.7)';

  return (
    <div className={styles.bottomBarStack}>
      {/* 4-tab capsule */}
      <div className={styles.bottomBarCapsule}>
        {CAPSULE_TABS.map((tab) => {
          const isDisabled = tab.disabledKey ? !dataFlags[tab.disabledKey] : false;
          const isActive = !isDisabled && activeSection === tab.id;
          const iconColor = isDisabled
            ? DISABLED_COLOR
            : isActive ? '#5AA5EF' : 'rgba(255, 255, 255, 0.7)';
          return (
            <div
              key={tab.id}
              className={`${styles.tabItem} ${isActive ? styles.tabItemActive : ''} ${isDisabled ? styles.tabItemDisabled : ''}`}
              onClick={() => handleTabClick(tab, isDisabled)}
              onKeyDown={(e) => { if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) handleTabClick(tab, false); }}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
            >
              <div className={styles.tabIconWrapper}>
                {tab.icon(iconColor)}
              </div>
              <span
                className={styles.tabLabel}
                style={isDisabled ? { color: DISABLED_COLOR } : undefined}
              >
                {tab.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Watchlist floating circle */}
      <div
        className={`${styles.watchlistCircle} ${isWatchlistActive ? styles.tabItemActive : ''}`}
        onClick={() => handleTabClick(WATCHLIST_TAB, false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTabClick(WATCHLIST_TAB, false); }}
        role="button"
        tabIndex={0}
      >
        <div className={styles.tabIconWrapper}>
          {WATCHLIST_TAB.icon(watchlistColor)}
        </div>
        <span className={styles.tabLabel} style={{ fontSize: '8px' }}>
          {WATCHLIST_TAB.label}
        </span>
      </div>
    </div>
  );
}

export default BottomBar;
